/**
 * Common error handling functions used by Backbone, jQuery, and XhrStream
 * by applying standard error handling to all requests.
 *
 * Provides checks for common concerns including:
 * - server offline
 * - expired login
 * - access denied
 *
 * It should not be invoked by components directly
 *
 * @see error-handler.js This should be the only component that is used by views.
 */
import _ from 'lodash'

import conductor from './conductor'
import HttpStatusCodes from 'http-status-codes'
import Xhr, {isOffline} from 'scoreboard/components/xhr'
import displayErrorHandler from 'scoreboard/components/display-error-handler'
import {isSpectating} from 'scoreboard/components/spectator'

var errors = {

	/**
	 * Potentially handles responses with an expired session
	 * @param {Xhr} xhr
	 * @return true if the session is expired and a login is required
	 */
	expiredLoginHandler: function(xhr) {
		if (Xhr.requiresLogin(xhr)) {
			// the login page is rendered on the server.  go to that page
			conductor.login()
			return true
		}
		return false
	},

	/**
	 * Potentially handles an XHR error.
	 *
	 * @param {Function} options.error
	 * 	If an error handler function is provided then the default error handler
	 * 	will not be applied.
	 *
	 * 	@param {Function} options.handler see displayErrorHandler
	 *
	 * @return true if the error was handled
	 */
	errorHandler: function(xhr, options = {}) {
		const {error} = options
		const noCustomHandler = !error
		const responseJSON = _.get(xhr, 'responseJSON')
		if (this.expiredLoginHandler(xhr)) {
			return true
		} else if (isOffline(xhr)) {
			displayErrorHandler(null)
			return true
		} else if (responseJSON && noCustomHandler) {
			if (xhr.status == HttpStatusCodes.FORBIDDEN && !isSpectating()) {
				_.extend(options, this.errorViewOptions(xhr.status, responseJSON))
			}
			displayErrorHandler(xhr, options)
			return true
		}
	},

	/**
	 * Given a response status and the response payload determine if the
	 * error should be displayed or some other message.  (e.g. Access Denied)
	 * @param {Number} status
	 * @param {Object} responseJSON
	 * @return {Object} to be passed to ErrorView as options
	 */
	errorViewOptions(status, responseJSON) {
		let options = responseJSON
		if (status == HttpStatusCodes.FORBIDDEN) {
			options = {
				title: 'common.security.accessDenied',
				message: responseJSON.message,
				messageCode: responseJSON.messageCode,
				stackTrace: responseJSON.stackTrace
			}
		}
		return options
	}

}

export default errors

errors.__filename__ = 'components/setup-errors.js'
